// Copyright 2026 Mo-Sys Engineering Ltd. All Rights Reserved.

import { addToArray, updateArrayItemByKey, removeArrayItemByKey } from '../utils/utils';

/**
 * =====================
 * Websocket events for Studio
 * =====================
 */

export const studioStateChangedState = (state, studio) => {
  const { studioState, studioUUID } = studio;
  if (state.studio?.studio_uuid === studioUUID) {
    state.studio.studioState = studioState;
    state.error.studio = studioState === 'Disconnected' ? 'Studio is currently disconnected' : null;
  }
};

export const studioConditionChangedState = (state, studio) => {
  const { studioCondition, studioUUID } = studio;
  if (state.studio?.studio_uuid === studioUUID) {
    state.studio.studioCondition = studioCondition;
    // clear error if studio is no longer 'On Air'
    state.error.studio = studioCondition === 'On Air' ? 'Studio is currently On Air' : null;
  }
};

export const sceneInStudioChangedState = (state, studioScene) => {
  const { studioUUID, sceneUUID, status, downloadPercentage } = studioScene;
  if (state.studio?.studio_uuid === studioUUID) {
    let changed = false;

    const newScenes = state.studio.studio_scenes.map(scene => {
      const studioScene = scene?.scene;
      if (studioScene.scene_uuid === sceneUUID) {
        const currentStatus = scene.status;
        const currentDownloadPercent = scene.download_percent || 0;
        const newDownloadPercent =
          status === 'downloading' || status === 'loading' ? downloadPercentage : 0;

        // Only update if something changed
        if (currentStatus !== status || currentDownloadPercent !== newDownloadPercent) {
          changed = true;
          return {
            ...scene,
            status,
            download_percent: newDownloadPercent,
          };
        }
      }
      return scene;
    });
    if (changed) {
      state.studio.studio_scenes = newScenes;
    }
    // else do nothing to avoid triggering unnecessary updates
  }
};

/**
 * =====================
 * Studio
 * =====================
 */

export const userStudiosState = (state, userStudios) => {
  const { studios } = userStudios;
  state.studios = studios;
};

export const studioByUuidState = (state, studio) => {
  // get studioCondition from the studios list for the current studio
  state.studio = {
    ...state.studio,
    ...studio,
    studioCondition:
      studio.studio_condition ||
      state.studios?.find(s => s.studio_uuid === studio.studio_uuid)?.studio_condition,
  };
};

export const studioEquipmentState = (state, studioEquipment) => {
  state.studio.studio_equipment = studioEquipment;
};

export const updatedStudioState = (state, updatedStudio) => {
  if (state.studio.studio_uuid === updatedStudio.studio_uuid) {
    state.studio = { ...state.studio, ...updatedStudio };
  }
  state.studios = updateArrayItemByKey(state.studios, 'studio_uuid', updatedStudio);
};

export const deleteStudioState = (state, deletedStudio) => {
  state.studios = removeArrayItemByKey(state.studios, 'studio_uuid', deletedStudio?.studio_uuid);
};

/**
 * =====================
 * Studio equipment
 * =====================
 */

export const addEquipmentState = (state, equipment) => {
  addToArray(state.studio.studio_equipment, equipment);
};

export const updateEquipmentState = (state, equipment) => {
  state.studio.studio_equipment = updateArrayItemByKey(
    state.studio.studio_equipment,
    'studio_equip_uuid',
    equipment
  );
};

export const deleteEquipmentState = (state, equipmentUuid) => {
  state.studio.studio_equipment = removeArrayItemByKey(
    state.studio.studio_equipment,
    'studio_equip_uuid',
    equipmentUuid
  );
};

/**
 * =====================
 * Studio scenes
 * =====================
 */

export const addSceneToStudioState = (state, scene) => {
  addToArray(state.studio.studio_scenes, scene);
};

export const deleteSceneFromStudioState = (state, sceneUuid) => {
  state.studio.studio_scenes = removeArrayItemByKey(
    state.studio.studio_scenes,
    'studio_scene_uuid',
    sceneUuid
  );
};

/**
 * =====================
 * Studio members
 * =====================
 */

export const studiosMembersState = (state, members) => {
  state.studio.members = members;
};

export const addStudioMemberState = (state, member) => {
  addToArray(state.studio.members, member);
};

export const deleteStudioMemberState = (state, studioMemberId) => {
  state.studio.members = removeArrayItemByKey(
    state.studio.members,
    'studio_member_id',
    studioMemberId
  );
};
